import { ApiError, api } from './api';

/**
 * **Was im Browser schiefgeht, landet in der Diagnose** (22. Oktober,
 * Veronika: „des lädt net -- Bildschirm schwarz“, und kein Wort davon im
 * Bericht).
 *
 * Bis dahin erfuhr niemand von einem Fehler in der Oberfläche: React räumt
 * bei einem Fehler beim Zeichnen alles ab, übrig bleibt der dunkle Grund. Jetzt
 * fängt `Auffangnetz` (main.tsx) den Absturz, zeigt, was passiert ist, und
 * meldet ihn -- ebenso Fehler, die außerhalb des Zeichnens auftreten.
 *
 * Gemeldet wird nur die Adresse ohne Anhang: an `?share=…` hängt der Zugang
 * eines Gastes, und der gehört in keinen Bericht.
 */

/** Höchstens so viele Meldungen je Seite -- ein Fehler in einer Schleife soll nicht den Server füllen. */
const HOECHSTENS = 5;
let gemeldet = 0;
const schonGemeldet = new Set<string>();

/** `oeffnen`: ein Projekt liess sich nicht öffnen (Editor.tsx, 7.263). */
export type FehlerArt = 'zeichnen' | 'skript' | 'versprechen' | 'oeffnen';

export function fehlerMelden(art: FehlerArt, fehler: unknown, komponenten?: string): void {
  if (gemeldet >= HOECHSTENS) return;
  const meldung = fehler instanceof Error ? `${fehler.name}: ${fehler.message}` : String(fehler);
  const stapel = fehler instanceof Error ? (fehler.stack ?? '') : '';
  // Derselbe Fehler zweimal (StrictMode, ein zweites Zeichnen) ist eine Meldung.
  const schluessel = `${art}|${meldung}`;
  if (schonGemeldet.has(schluessel)) return;
  schonGemeldet.add(schluessel);
  gemeldet++;
  const daten = {
    anlass: 'fehler',
    art,
    meldung: meldung.slice(0, 500),
    stapel: stapel.slice(0, 3000),
    komponenten: (komponenten ?? '').slice(0, 2000),
    adresse: location.pathname,
    fenster: { breite: window.innerWidth, hoehe: window.innerHeight },
  };
  void api.messwerte({ projectId: null, ua: navigator.userAgent, daten }).catch(() => undefined);
}

let angemeldet = false;

/** Fehler außerhalb des Zeichnens: im Skript und in Versprechen, die niemand abfängt. */
export function fehlerAuffangen(): void {
  if (angemeldet) return;
  angemeldet = true;
  window.addEventListener('error', (e) => {
    // Ein Bild oder Video, das nicht lädt, ist kein Fehler der App -- und
    // „ResizeObserver loop …“ ist eine Warnung des Browsers, keine.
    if (!e.error && !e.message) return;
    if (/ResizeObserver loop/.test(e.message ?? '')) return;
    fehlerMelden('skript', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const grund: unknown = e.reason;
    // Abgebrochene Anfragen (Seite gewechselt, Laden gestoppt) sind gewollt;
    // eine Antwort des Servers steht schon in seinem Protokoll, und ein
    // fehlendes Netz ist kein Fehler der App. Sonst füllten sie die fünfzehn
    // Plätze „Im Browser“, und der eine Absturz, um den es geht, fiele heraus.
    if (grund instanceof DOMException && grund.name === 'AbortError') return;
    if (grund instanceof ApiError) return;
    if (grund instanceof TypeError && /fetch|Load failed|NetworkError|network/i.test(grund.message)) return;
    fehlerMelden('versprechen', grund);
  });
}
